import { useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  CalendarDays,
  ChevronDown,
  CircleDot,
  FileSpreadsheet,
  Flame,
  LogIn,
  LogOut,
  MapPinned,
  PawPrint,
  RotateCcw,
} from "lucide-react";

import {
  exporteerHerkomst,
  haalHerkomst,
  lijstSoorten,
  type Herkomst,
  type HerkomstNiveau,
  type HerkomstPlek,
  type HerkomstRichting,
} from "../api/insights";
import { HerkomstKaartMap, type Weergave } from "../components/HerkomstKaartMap";
import { DIERSOORTEN, Filter, PilLijst, useInklappenErnaast } from "./DataInsightFilters";

/**
 * De herkomstkaart: waar de dieren vandaan komen, of waar ze naartoe gaan.
 *
 * <p>Een eigen tabblad en geen weergave van een standaardanalyse. Een analyse telt langs een as, de kaart
 * telt per plek; de filters die daar zin hebben (uitsplitsen, per maand of per week) hebben dat hier niet,
 * en een filterbalk met knoppen die niets doen is erger dan een eigen, kortere balk.
 *
 * <p>De balk ziet er wel hetzelfde uit als die van de analyses — dezelfde pillen, dezelfde kleur per
 * filter — zodat "diersoort" op beide tabbladen op dezelfde plek en in dezelfde kleur staat. Detail en
 * weergave staan op de kaart zelf: die veranderen hoe je kijkt, niet wat er geteld wordt.
 *
 * <p>Wat er niet op de kaart staat wordt ernaast genoemd. Zonder die regels lijkt een lege streek een
 * streek zonder dieren, terwijl er misschien alleen geen gemeente of adres is ingevuld.
 */

/** Na hoeveel tijd het scherm opnieuw vraagt terwijl de server nog plekken opzoekt. */
export const OPNIEUW_NA_MS = 3000;
/** Hoe vaak, zodat een blijvende storing bij het opzoeken het scherm niet eindeloos laat vragen. */
const MAX_POGINGEN = 40;

type Snelkeuze = "12M" | "DIT_JAAR" | "VORIG_JAAR" | "5J";

const PERIODES: { sleutel: Snelkeuze; label: string }[] = [
  { sleutel: "12M", label: "herkomst.laatste12" },
  { sleutel: "DIT_JAAR", label: "herkomst.ditJaar" },
  { sleutel: "VORIG_JAAR", label: "herkomst.vorigJaar" },
  { sleutel: "5J", label: "herkomst.laatste5" },
];

/** De teksten die met de richting meewisselen: een vertrek is geen opname, en heeft geen vindplaats. */
const TEKSTEN: Record<HerkomstRichting, {
  titel: string; uitleg: string; totaal: string; meeste: string; eenheid: string;
  zonderGemeente: string; zonderPostcode: string; nietGevonden: string; leeg: string;
}> = {
  OPNAME: {
    titel: "herkomst.titel", uitleg: "herkomst.uitleg", totaal: "herkomst.totaal",
    meeste: "herkomst.meesteHerkomst", eenheid: "herkomst.opnames",
    zonderGemeente: "herkomst.zonderGemeente", zonderPostcode: "herkomst.zonderPostcode",
    nietGevonden: "herkomst.nietGevonden", leeg: "herkomst.leeg",
  },
  VERTREK: {
    titel: "herkomst.titelVertrek", uitleg: "herkomst.uitlegVertrek", totaal: "herkomst.totaalVertrek",
    meeste: "herkomst.meesteBestemming", eenheid: "herkomst.vertrekken",
    zonderGemeente: "herkomst.zonderAdres", zonderPostcode: "herkomst.zonderPostcodeVertrek",
    nietGevonden: "herkomst.nietGevondenVertrek", leeg: "herkomst.leegVertrek",
  },
};

/** De lokale datum als ISO; toISOString rekent naar UTC en schuift rond middernacht een dag op. */
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Het bereik van een snelkeuze, op de dag af. */
export function bereikVan(keuze: Snelkeuze, vandaag = new Date()): { van: string; tot: string } {
  const jaar = vandaag.getFullYear();
  if (keuze === "DIT_JAAR") return { van: `${jaar}-01-01`, tot: iso(vandaag) };
  if (keuze === "VORIG_JAAR") return { van: `${jaar - 1}-01-01`, tot: `${jaar - 1}-12-31` };
  const begin = new Date(vandaag);
  begin.setFullYear(jaar - (keuze === "5J" ? 5 : 1));
  begin.setDate(begin.getDate() + 1);
  return { van: iso(begin), tot: iso(vandaag) };
}

/** Een schakelaar: twee of meer knoppen waarvan er één aan staat. */
function Schakelaar<T extends string>({ label, waarde, opties, onKies, klasse = "fawkes-herkomst__schakelaar" }: {
  label: string;
  waarde: T;
  opties: { waarde: T; tekst: string; icoon?: React.ReactNode }[];
  onKies: (w: T) => void;
  klasse?: string;
}) {
  return (
    <div className={klasse} role="group" aria-label={label}>
      {opties.map((o) => (
        <button key={o.waarde} type="button" aria-pressed={waarde === o.waarde} onClick={() => onKies(o.waarde)}>
          {o.icoon}
          {o.tekst}
        </button>
      ))}
    </div>
  );
}

/**
 * Opname of vertrek, en welke soorten daarvan.
 *
 * <p>Eén pil en niet twee: de soorten horen bij de richting. Bovenin het uitklapvenster kies je opname of
 * vertrek, daaronder vink je de soorten aan. Omschakelen wist de aangevinkte soorten — "Zwerver" is geen
 * soort vertrek, en een vertrekkaart met een opnamefilter erop zou stilletjes leeg zijn.
 */
function RichtingKeuze({ richting, soorten, opties, onRichting, onSoorten, t }: {
  richting: HerkomstRichting;
  soorten: string[];
  opties: string[];
  onRichting: (r: HerkomstRichting) => void;
  onSoorten: (s: string[]) => void;
  t: (key: string) => string;
}) {
  const naam = t(richting === "OPNAME" ? "herkomst.opname" : "herkomst.vertrek");
  const welke = soorten.length === 0 ? t("herkomst.alleSoortenKort")
    : soorten.length <= 2 ? soorten.join(", ") : `${soorten.length} ${t("herkomst.gekozen")}`;
  const inklap = useInklappenErnaast();
  return (
    <div className="fawkes-di-filter">
      <span className="fawkes-di-filter__label">{t("herkomst.richting")}</span>
      <details ref={inklap} className="fawkes-di-pil fawkes-di-pil--lijst fawkes-di-pil--as">
        <summary className="fawkes-di-pil__samenvatting">
          <span className="fawkes-di-pil__icoon" aria-hidden="true">
            {richting === "OPNAME" ? <LogIn /> : <LogOut />}
          </span>
          <span className="fawkes-di-pil__waarde">{`${naam} · ${welke}`}</span>
          <ChevronDown className="fawkes-di-pil__chevron" aria-hidden="true" />
        </summary>
        <div className="fawkes-di-pil__hokjes fawkes-herkomst__richtingvenster">
          <Schakelaar
            klasse="fawkes-herkomst__schakelaar fawkes-herkomst__richting"
            label={t("herkomst.richting")}
            waarde={richting}
            onKies={onRichting}
            opties={[
              { waarde: "OPNAME", tekst: t("herkomst.opname"), icoon: <LogIn aria-hidden="true" /> },
              { waarde: "VERTREK", tekst: t("herkomst.vertrek"), icoon: <LogOut aria-hidden="true" /> },
            ]}
          />
          <span className="fawkes-herkomst__hokjeskop">
            {t(richting === "OPNAME" ? "herkomst.soortOpname" : "herkomst.soortVertrek")}
          </span>
          {opties.map((o) => (
            <label className="fawkes-di-pil__hokje" key={o}>
              <input
                type="checkbox"
                checked={soorten.includes(o)}
                onChange={(e) => onSoorten(e.target.checked ? [...soorten, o] : soorten.filter((x) => x !== o))}
              />
              <span>{o}</span>
            </label>
          ))}
          {opties.length === 0 ? <span className="fawkes-herkomst__noot">{t("herkomst.geenSoorten")}</span> : null}
        </div>
      </details>
    </div>
  );
}

export function HerkomstKaart({ vestigingen, t }: {
  /** De vestigingen waar deze gebruiker bij mag; bij één is er niets te kiezen. */
  vestigingen: string[];
  t: (key: string) => string;
}) {
  const [periode, setPeriode] = useState<Snelkeuze | null>("12M");
  const [van, setVan] = useState(() => bereikVan("12M").van);
  const [tot, setTot] = useState(() => bereikVan("12M").tot);
  const [diersoorten, setDiersoorten] = useState<string[]>([]);
  const [richting, setRichting] = useState<HerkomstRichting>("OPNAME");
  const [soorten, setSoorten] = useState<string[]>([]);
  const [gekozenVestigingen, setGekozenVestigingen] = useState<string[]>([]);
  const [niveau, setNiveau] = useState<HerkomstNiveau>("GEMEENTE");
  const [weergave, setWeergave] = useState<Weergave>("WARMTE");
  const [soortLijsten, setSoortLijsten] = useState<Record<HerkomstRichting, string[]>>({ OPNAME: [], VERTREK: [] });
  const [uitkomst, setUitkomst] = useState<Herkomst | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [ronde, setRonde] = useState(0);
  const [exporteert, setExporteert] = useState(false);
  const pogingen = useRef(0);
  const vorigVerzoek = useRef("");

  useEffect(() => {
    // Beide lijsten meteen: omschakelen hoort de soorten direct te laten zien. Mislukt er een, dan staat
    // alleen die lijst leeg; de kaart zelf doet het nog.
    for (const r of ["OPNAME", "VERTREK"] as const) {
      lijstSoorten(r).then((l) => setSoortLijsten((s) => ({ ...s, [r]: l }))).catch(() => {});
    }
  }, []);

  const verzoek = useMemo(() => ({
    vanDatum: van || null,
    totDatum: tot || null,
    diersoorten,
    richting,
    soorten,
    vestigingen: gekozenVestigingen,
    niveau,
  }), [van, tot, diersoorten, richting, soorten, gekozenVestigingen, niveau]);
  const verzoekSleutel = JSON.stringify(verzoek);

  useEffect(() => {
    let vervallen = false;
    // Alleen bij een andere vraag "bezig" tonen: het opnieuw vragen terwijl plekken worden opgezocht
    // hoort de kaart stil aan te vullen, niet elke drie seconden te knipperen.
    if (vorigVerzoek.current !== verzoekSleutel) {
      vorigVerzoek.current = verzoekSleutel;
      pogingen.current = 0;
      setBezig(true);
      setFout(null);
    }
    haalHerkomst(verzoek)
      .then((u) => { if (!vervallen) setUitkomst(u); })
      .catch((e: unknown) => {
        if (!vervallen) setFout(e instanceof Error ? e.message : t("dataInsight.nietBeschikbaar"));
      })
      .finally(() => { if (!vervallen) setBezig(false); });
    return () => { vervallen = true; };
    // Op de sleutel en niet op het object: dat is elke hertekening nieuw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verzoekSleutel, ronde]);

  useEffect(() => {
    if (!uitkomst || uitkomst.nogOpzoeken === 0 || pogingen.current >= MAX_POGINGEN) return;
    const klok = window.setTimeout(() => {
      pogingen.current += 1;
      setRonde((r) => r + 1);
    }, OPNIEUW_NA_MS);
    return () => window.clearTimeout(klok);
  }, [uitkomst]);

  const kiesPeriode = (keuze: Snelkeuze) => {
    const bereik = bereikVan(keuze);
    setPeriode(keuze);
    setVan(bereik.van);
    setTot(bereik.tot);
  };

  const kiesRichting = (r: HerkomstRichting) => {
    if (r === richting) return;
    setRichting(r);
    setSoorten([]);
  };

  // Terug naar de uitgangsstand. Detail en weergave blijven staan: dat is hoe je kijkt, en wie de
  // filters wegdoet wil de afbakening kwijt, niet de kaart die hij net had ingesteld.
  const herstel = () => {
    kiesPeriode("12M");
    setDiersoorten([]);
    setRichting("OPNAME");
    setSoorten([]);
    setGekozenVestigingen([]);
  };

  // Met dezelfde filters als de kaart op het scherm: het bestand hoort te zijn wat je ziet, met de regels erbij.
  const exporteer = () => {
    setExporteert(true);
    setFout(null);
    exporteerHerkomst(verzoek)
      .catch((e: unknown) => setFout(e instanceof Error ? e.message : t("dataInsight.nietBeschikbaar")))
      .finally(() => setExporteert(false));
  };

  const samenvatting = (gekozen: string[], leeg: string) =>
    gekozen.length === 0 ? leeg
      : gekozen.length <= 2 ? gekozen.join(", ")
        : `${gekozen.length} ${t("herkomst.gekozen")}`;

  const tekst = TEKSTEN[richting];
  const plekken = uitkomst?.plekken ?? [];
  const perPostcode = (uitkomst?.niveau ?? niveau) === "POSTCODE";
  const opKaart = plekken.reduce((s, p) => s + p.aantal, 0);
  const grootste = plekken.length > 0 ? plekken[0].aantal : 0;
  const getal = (n: number) => n.toLocaleString("nl-NL");
  const labelVan = (p: HerkomstPlek) => (perPostcode ? `${t("herkomst.postcode")} ${p.naam}` : p.naam);
  const procent = uitkomst && uitkomst.totaal > 0 ? Math.round((opKaart / uitkomst.totaal) * 100) : 0;

  return (
    <div className="fawkes-herkomst">
      <form className="fawkes-di-filters fawkes-herkomst__filters" onSubmit={(e) => e.preventDefault()}>
        <div className="fawkes-di-filters__kop">
          <span className="fawkes-di-filters__kopicoon" aria-hidden="true"><MapPinned /></span>
          <div className="fawkes-di-filters__koptekst">
            <h2 className="fawkes-di-filters__koptitel">{t(tekst.titel)}</h2>
            <p className="fawkes-di-filters__kopsub">{t(tekst.uitleg)}</p>
          </div>
          <button
            type="button"
            className="fawkes-di-filters__herstel"
            onClick={exporteer}
            disabled={exporteert || !uitkomst}
            aria-busy={exporteert}
          >
            <FileSpreadsheet aria-hidden="true" />
            {t("herkomst.excel")}
          </button>
          <button type="button" className="fawkes-di-filters__herstel" onClick={herstel}>
            <RotateCcw aria-hidden="true" />
            {t("herkomst.herstel")}
          </button>
        </div>

        <div className="fawkes-di-periode">
          <div className="fawkes-di-periode__regel">
            <Filter
              kleur="tijd"
              icoon={<CalendarDays />}
              naam={t("herkomst.periode")}
              kind={
                <span className="fawkes-di-pil__periode">
                  <input
                    type="date" className="fawkes-di-pil__datum" value={van} aria-label={t("herkomst.van")}
                    onChange={(e) => { setVan(e.target.value); setPeriode(null); }}
                  />
                  <span className="fawkes-di-pil__tot">{t("herkomst.tot")}</span>
                  <input
                    type="date" className="fawkes-di-pil__datum" value={tot} aria-label={t("herkomst.totDatum")}
                    onChange={(e) => { setTot(e.target.value); setPeriode(null); }}
                  />
                </span>
              }
            />
          </div>
          <div className="fawkes-di-periode__snel" role="group" aria-label={t("herkomst.periode")}>
            {PERIODES.map((p) => (
              <button
                key={p.sleutel}
                type="button"
                aria-pressed={periode === p.sleutel}
                className={"fawkes-di-periode__keuze" + (periode === p.sleutel ? " fawkes-di-periode__keuze--aan" : "")}
                onClick={() => kiesPeriode(p.sleutel)}
              >
                {t(p.label)}
              </button>
            ))}
          </div>
        </div>

        <div className="fawkes-di-filters__pillen">
          <PilLijst
            kleur="soort"
            icoon={<PawPrint />}
            naam={t("herkomst.diersoort")}
            samenvatting={samenvatting(diersoorten, t("herkomst.alleSoorten"))}
            opties={DIERSOORTEN}
            gekozen={diersoorten}
            onZet={setDiersoorten}
          />

          <RichtingKeuze
            richting={richting}
            soorten={soorten}
            opties={soortLijsten[richting]}
            onRichting={kiesRichting}
            onSoorten={setSoorten}
            t={t}
          />

          {/* Niets aangevinkt is de vestiging waar je in zit, niet "alle": wat je mag zien bepaalt de
              server, net als bij de analyses. */}
          {vestigingen.length > 1 ? (
            <PilLijst
              kleur="vestiging"
              icoon={<Building2 />}
              naam={t("herkomst.vestigingen")}
              samenvatting={vestigingen.every((v) => gekozenVestigingen.includes(v))
                ? t("herkomst.alleVestigingen")
                : samenvatting(gekozenVestigingen, t("herkomst.huidigeVestiging"))}
              alle={t("herkomst.alleVestigingen")}
              opties={vestigingen}
              gekozen={gekozenVestigingen}
              onZet={setGekozenVestigingen}
            />
          ) : null}
        </div>
      </form>

      {fout ? <p className="fawkes-di__fout" role="alert">{fout}</p> : null}

      <div className="fawkes-herkomst__inhoud">
        <div className="fawkes-herkomst__kaart">
          <HerkomstKaartMap
            plekken={plekken}
            weergave={weergave}
            labelVan={labelVan}
            eenheid={t(tekst.eenheid)}
            pasSleutel={`${richting}|${niveau}|${gekozenVestigingen.join(",")}`}
            volledig={(uitkomst?.nogOpzoeken ?? 0) === 0}
          />
          <div className="fawkes-herkomst__kaartknoppen">
            <Schakelaar
              label={t("herkomst.niveau")}
              waarde={niveau}
              onKies={setNiveau}
              opties={[
                { waarde: "GEMEENTE", tekst: t("herkomst.perGemeente") },
                { waarde: "POSTCODE", tekst: t("herkomst.perPostcode") },
              ]}
            />
            <Schakelaar
              label={t("herkomst.weergave")}
              waarde={weergave}
              onKies={setWeergave}
              opties={[
                { waarde: "WARMTE", tekst: t("herkomst.warmte"), icoon: <Flame aria-hidden="true" /> },
                { waarde: "BOLLEN", tekst: t("herkomst.bollen"), icoon: <CircleDot aria-hidden="true" /> },
              ]}
            />
          </div>
          {weergave === "WARMTE" && plekken.length > 0 ? (
            <div className="fawkes-herkomst__legenda" aria-hidden="true">
              <span>{t("herkomst.weinig")}</span>
              <span className="fawkes-herkomst__schaal" />
              <span>{t("herkomst.veel")}</span>
            </div>
          ) : null}
          {bezig ? <span className="fawkes-herkomst__bezig" role="status">{t("common.loading")}</span> : null}
        </div>

        <aside className="fawkes-herkomst__zij">
          <div className="fawkes-herkomst__cijfer">
            <span className="fawkes-herkomst__label">{t(tekst.totaal)}</span>
            <strong className="fawkes-herkomst__getal">{uitkomst ? getal(uitkomst.totaal) : "—"}</strong>
            {uitkomst && uitkomst.totaal > 0 ? (
              <span className="fawkes-herkomst__noot">
                {t("herkomst.opDeKaart")}: {getal(opKaart)} ({procent}%)
              </span>
            ) : null}
          </div>

          {plekken.length > 0 ? (
            <div>
              <h3 className="fawkes-herkomst__lijstkop">{t(tekst.meeste)}</h3>
              <ol className="fawkes-herkomst__lijst">
                {plekken.slice(0, 10).map((p) => (
                  <li key={p.naam} className="fawkes-herkomst__regel">
                    <span>{labelVan(p)}</span>
                    <span className="fawkes-herkomst__aantal">{getal(p.aantal)}</span>
                    <span className="fawkes-herkomst__balk" aria-hidden="true">
                      <span style={{ width: `${grootste > 0 ? (p.aantal / grootste) * 100 : 0}%` }} />
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : uitkomst && !bezig && uitkomst.nogOpzoeken === 0 ? (
            <p className="fawkes-herkomst__noot">{t(tekst.leeg)}</p>
          ) : null}

          {uitkomst && uitkomst.nogOpzoeken > 0 ? (
            <p className="fawkes-herkomst__noot" role="status">
              {getal(uitkomst.nogOpzoeken)} {t("herkomst.nogOpzoeken")}
            </p>
          ) : null}
          {uitkomst && uitkomst.zonderLocatie > 0 ? (
            <p className="fawkes-herkomst__noot">
              {getal(uitkomst.zonderLocatie)} {t(perPostcode ? tekst.zonderPostcode : tekst.zonderGemeente)}
            </p>
          ) : null}
          {uitkomst && uitkomst.nietGevonden > 0 ? (
            <p className="fawkes-herkomst__noot">
              {getal(uitkomst.nietGevonden)} {t(tekst.nietGevonden)}
            </p>
          ) : null}
          {niveau === "POSTCODE" && richting === "OPNAME" ? (
            <p className="fawkes-herkomst__noot">{t("herkomst.postcodeUitleg")}</p>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
